import { c as _c } from "react/compiler-runtime";
import { TabContext, TabList } from "@mui/lab";
import { alpha, Box, Stack, Tab, Typography, useTheme } from "@mui/material";
import React, { useState } from "react";
import { selectCallstackError, selectCallstackIsReady, selectCallstackResult, selectCallstackRuntime, selectConsoleLogs } from "#/features/callstack/model/callstackSlice";
import { CallstackTable } from "#/features/callstack/ui/CallstackTable";
import { useI18nContext } from "#/shared/hooks";
import { LoadingSkeletonOverlay } from "#/shared/ui/atoms/LoadingSkeletonOverlay";
import { PanelWrapper } from "#/shared/ui/templates/PanelWrapper";
import { StyledTabPanel } from "#/shared/ui/templates/StyledTabPanel";
import { TabListWrapper } from "#/shared/ui/templates/TabListWrapper";
import { useAppSelector } from "#/store/hooks";
const ConsoleOutput: React.FC = () => {
  const $ = _c(17);
  const theme = useTheme();
  const {
    LL
  } = useI18nContext();
  const logs = useAppSelector(selectConsoleLogs);
  if (!logs.length) {
    return null;
  }
  let t0;
  if ($[0] !== LL) {
    t0 = LL.CONSOLE_OUTPUT();
    $[0] = LL;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== theme.palette.info.dark) {
    t1 = alpha(theme.palette.info.dark, 0.08);
    $[2] = theme.palette.info.dark;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      whiteSpace: "pre-wrap"
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1) {
    t3 = {
      backgroundColor: t1,
      p: 1,
      borderRadius: 2,
      div: t2
    };
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== logs) {
    t4 = logs.map(_temp);
    $[7] = logs;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <pre>{t4}</pre>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = <Box component="div" sx={t3}>{t5}</Box>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t0 || $[15] !== t6) {
    t7 = <Typography variant="caption">{t0}:{t6}</Typography>;
    $[14] = t0;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
const ResultData: React.FC<{
  result: string | number | null;
}> = t0 => {
  const $ = _c(14);
  const {
    result
  } = t0;
  const theme = useTheme();
  const {
    LL
  } = useI18nContext();
  let t1;
  if ($[0] !== LL) {
    t1 = LL.RETURNED();
    $[0] = LL;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== theme.palette.success.dark) {
    t2 = alpha(theme.palette.success.dark, 0.2);
    $[2] = theme.palette.success.dark;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = {
      backgroundColor: t2,
      p: 1,
      borderRadius: 2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== result) {
    t4 = <pre>{result}</pre>;
    $[6] = result;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <Box component="div" sx={t3}>{t4}</Box>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t5) {
    t6 = <Typography variant="caption">{t1}:{t5}</Typography>;
    $[11] = t1;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
export type OutputPanelProps = {
  /** Optional actions to render in the tab header (e.g. collapse toggle on mobile) */
  trailingHeaderActions?: React.ReactNode;
  /** From SplitPanelsLayout - panel vertical size (unused, for type compatibility) */
  verticalSize?: number;
  /** Controlled tab value ("1" = Output, "2" = Callstack) */
  value?: string;
  /** Called when tab changes (for controlled mode) */
  onTabChange?: (newValue: string) => void;
  /** When true, render only the tab bar (for collapsed state on mobile) */
  headerOnly?: boolean;
  /** When headerOnly, called when the tab bar is clicked (e.g. to expand on active tab click) */
  onTabBarClick?: () => void;
};
export const OutputPanel: React.FC<OutputPanelProps> = t0 => {
  const $ = _c(50);
  const {
    trailingHeaderActions,
    value: controlledValue,
    onTabChange,
    headerOnly: t1,
    onTabBarClick
  } = t0;
  const headerOnly = t1 === undefined ? false : t1;
  const [internalValue, setInternalValue] = useState("1");
  const value = controlledValue ?? internalValue;
  const {
    LL
  } = useI18nContext();
  let t2;
  if ($[0] !== controlledValue || $[1] !== onTabChange) {
    t2 = (_, newValue) => {
      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }
      onTabChange?.(newValue);
    };
    $[0] = controlledValue;
    $[1] = onTabChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleChange = t2;
  const isReady = useAppSelector(selectCallstackIsReady);
  const runtime = useAppSelector(selectCallstackRuntime);
  const result = useAppSelector(selectCallstackResult);
  const error = useAppSelector(selectCallstackError);
  let t3;
  if ($[3] !== headerOnly) {
    t3 = headerOnly ? {
      boxShadow: "none"
    } : undefined;
    $[3] = headerOnly;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== LL) {
    t4 = LL.PANEL_TABS();
    $[5] = LL;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== LL) {
    t5 = LL.OUTPUT();
    $[7] = LL;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <Tab label={t5} value="1" />;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== LL) {
    t7 = LL.CALLSTACK();
    $[11] = LL;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <Tab label={t7} value="2" />;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== handleChange || $[16] !== t4 || $[17] !== t6 || $[18] !== t8) {
    t9 = <TabList onChange={handleChange} aria-label={t4}>{t6}{t8}</TabList>;
    $[15] = handleChange;
    $[16] = t4;
    $[17] = t6;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== headerOnly || $[21] !== trailingHeaderActions) {
    t10 = !headerOnly && trailingHeaderActions && <Stack direction="row" sx={{
      alignItems: "center"
    }}>{trailingHeaderActions}</Stack>;
    $[20] = headerOnly;
    $[21] = trailingHeaderActions;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t3 || $[25] !== t9) {
    t11 = <TabListWrapper sx={t3}>{t9}{t10}</TabListWrapper>;
    $[23] = t10;
    $[24] = t3;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const tabBar = t11;
  if (headerOnly) {
    const t12 = onTabBarClick ? "pointer" : undefined;
    let t13;
    if ($[27] !== t12) {
      t13 = {
        flexShrink: 0,
        cursor: t12
      };
      $[27] = t12;
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    let t14;
    if ($[29] !== tabBar || $[30] !== value) {
      t14 = <TabContext value={value}>{tabBar}</TabContext>;
      $[29] = tabBar;
      $[30] = value;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    let t15;
    if ($[32] !== onTabBarClick || $[33] !== t13 || $[34] !== t14) {
      t15 = <Box sx={t13} onClick={onTabBarClick}>{t14}</Box>;
      $[32] = onTabBarClick;
      $[33] = t13;
      $[34] = t14;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    return t15;
  }
  let t12;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <LoadingSkeletonOverlay />;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      height: "100%"
    };
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== LL || $[39] !== error || $[40] !== isReady || $[41] !== result || $[42] !== runtime) {
    t14 = <StyledTabPanel value="1" style={t13}><Stack spacing={2}>{!isReady ? <Box sx={{
          display: "box"
        }}><Typography variant="h5">{LL.NO_DATA()}</Typography><Typography variant="caption">{LL.YOU_NEED_TO_RUN_THE_CODE_FIRST()}</Typography></Box> : error ? <Box sx={{
          display: "box"
        }}><Typography variant="h5" color="error">{error.name}: {error.message}</Typography>{error.stack && <Typography variant="caption" color="error">{error.stack}</Typography>}</Box> : <Stack spacing={1}><Stack direction="row" spacing={1}><Typography variant="h5" sx={{
              color: "success.main"
            }}>{LL.SUCCESS()}</Typography>{typeof runtime === "number" && <Typography variant="caption" sx={{
              color: "text.disabled",
              mt: 1
            }}>{LL.RUNTIME()}: {runtime.toFixed(2)} {LL.MS()}</Typography>}</Stack><ConsoleOutput /><ResultData result={result} /></Stack>}</Stack></StyledTabPanel>;
    $[38] = LL;
    $[39] = error;
    $[40] = isReady;
    $[41] = result;
    $[42] = runtime;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      p: 0,
      flex: 1,
      minHeight: 0
    };
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <StyledTabPanel value="2" sx={t15} useScroll={false}><CallstackTable /></StyledTabPanel>;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t14 || $[47] !== tabBar || $[48] !== value) {
    t17 = <PanelWrapper>{t12}<TabContext value={value}>{tabBar}{t14}{t16}</TabContext></PanelWrapper>;
    $[46] = t14;
    $[47] = tabBar;
    $[48] = value;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  return t17;
};
function _temp(log) {
  return <div key={log.id}>{log.args.join(" ")}</div>;
}